import { useCallback, useRef } from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import ShortUniqueId from 'short-unique-id';

// Same shape as wb-admin-web's own createInvite mutation
// (app/components/Screens/FalkorGame/GameDetail/createInvite.js) - token
// selected here too (unlike that one) since this hook needs the created
// invite's shareable token back immediately, not via a follow-up list
// query refetch.
export const query = gql`
  mutation CreateInvite(
    $gameId: ID
    $onboardingVariant: String = "noflow"
    $token: String!
    $type: InviteType = GenericInvite
  ) {
    createInvite(
      token: $token
      type: $type
      gameId: $gameId
      onboardingVariant: $onboardingVariant
    ) {
      id
      token
    }
  }
`;

// Creates a new Invite for the current user, generating its token
// client-side the same way wb-admin-web's "Host Share URLs" table does (a
// random 10-char ShortUniqueId). userId is deliberately not an argument
// here - wb-graphql-service's createInvite resolver defaults it to the
// caller's own jwt sub when omitted, which is exactly what a self-serve
// "bring someone" share link needs.
export const useCreateInvite = () => {
  const uid = useRef(new ShortUniqueId({ length: 10 }));
  const [mutate, { loading, error }] = useMutation(query);

  const createInvite = useCallback(
    async ({
      gameId,
      type = 'GenericInvite',
      onboardingVariant = 'noflow',
    } = {}) => {
      const { data } = await mutate({
        variables: {
          token: uid.current.rnd(),
          gameId: gameId == null ? undefined : String(gameId),
          type,
          onboardingVariant,
        },
      });

      return data?.createInvite;
    },
    [mutate]
  );

  return { createInvite, loading, error };
};
